import React, { useEffect, useMemo, useState } from "react";
import { listRooms, getRoomAssignment } from "./happyschoolApi";

// Phân phòng học theo tuần: cells["{roomId}|{day}|{si}"] = { classId, type } (si 0 = sáng, 1 = chiều).
// 1 lớp có thể ở 2 phòng cùng buổi (lớp nghề chia nhóm).
export const ROOM_TYPES = [
  { key: "nghe", label: "Nghề", bg: "#fecaca", fg: "#991b1b" },
  { key: "chinhkhoa", label: "Chính khoá", bg: "#fef08a", fg: "#854d0e" },
  { key: "tangcuong", label: "Tăng cường", bg: "#bbf7d0", fg: "#166534" },
];
export const ROOM_TYPE_BY_KEY = Object.fromEntries(ROOM_TYPES.map((t) => [t.key, t]));

// "201 (A3)" — phòng không ghi toà nhà thì chỉ tên phòng.
export const roomLabel = (r) => (r ? `${r.name}${r.building ? ` (${r.building})` : ""}` : "");

// "{classId}|{day}|{si}" -> [{ roomId, type }] (theo thứ tự danh sách phòng).
export function indexByClass(cells, rooms) {
  const order = Object.fromEntries(rooms.map((r, i) => [r.id, i]));
  const out = {};
  Object.entries(cells || {}).forEach(([k, c]) => {
    const [roomId, day, si] = k.split("|");
    const key = `${c.classId}|${day}|${si}`;
    (out[key] = out[key] || []).push({ roomId, type: c.type });
  });
  Object.values(out).forEach((a) => a.sort((x, y) => (order[x.roomId] ?? 1e9) - (order[y.roomId] ?? 1e9)));
  return out;
}

// Tải danh sách phòng + phân phòng của 1 tuần; roomOf(classId, day, si) -> { room, label, type } | null
// (lớp ở nhiều phòng -> label ghép "404 (A3), 302 (A4)", type/room lấy phòng đầu).
export function useRoomAssignment(schoolId, weekId, reloadKey = 0) {
  const [rooms, setRooms] = useState([]);
  const [cells, setCells] = useState({});
  useEffect(() => {
    if (!schoolId || !weekId) { setRooms([]); setCells({}); return; }
    let stale = false;
    Promise.all([listRooms(schoolId).catch(() => []), getRoomAssignment(schoolId, weekId).catch(() => ({ cells: {} }))]).then(([r, a]) => {
      if (stale) return;
      setRooms(r || []);
      setCells((a && a.cells) || {});
    });
    return () => { stale = true; };
  }, [schoolId, weekId, reloadKey]);
  const roomById = useMemo(() => Object.fromEntries(rooms.map((r) => [r.id, r])), [rooms]);
  const byClass = useMemo(() => indexByClass(cells, rooms), [cells, rooms]);
  const roomOf = (classId, day, si) => {
    const list = (byClass[`${classId}|${day}|${si}`] || []).filter((x) => roomById[x.roomId]);
    if (!list.length) return null;
    return { room: roomById[list[0].roomId], label: list.map((x) => roomLabel(roomById[x.roomId])).join(", "), type: list[0].type };
  };
  const hasAny = Object.keys(cells).length > 0;
  return { rooms, cells, roomById, roomOf, hasAny };
}

// Dòng "🚪 Phòng" đặt ĐẦU mỗi buổi trên TKB 1 lớp: ô nhãn buổi (rowSpan cả buổi + dòng này) + phòng từng thứ.
export function RoomRow({ classId, si, rowSpan, sesLabel, roomOf, labelStyle, cellStyle, dayCount = 6 }) {
  return (
    <tr>
      <td rowSpan={rowSpan} style={labelStyle}>{sesLabel}</td>
      <td style={{ ...cellStyle, position: "sticky", left: 60, zIndex: 5, background: "#f8fafc", whiteSpace: "nowrap", fontWeight: 600, color: "#475569" }}>🚪 Phòng</td>
      {Array.from({ length: dayCount }, (_, day) => {
        const r = roomOf(classId, day, si);
        const t = r && ROOM_TYPE_BY_KEY[r.type];
        return (
          <td key={day} title={r ? [t && t.label, r.room.note].filter(Boolean).join(" · ") : "Chưa xếp phòng"} style={{ ...cellStyle, background: "#f8fafc", verticalAlign: "middle" }}>
            {r ? <span style={{ background: t ? t.bg : "#e5e7eb", color: t ? t.fg : "#374151", borderRadius: 4, padding: "0 6px", fontWeight: 700, fontSize: 11 }}>{r.label}</span> : <span style={{ color: "#cbd5e1" }}>—</span>}
          </td>
        );
      })}
    </tr>
  );
}
